import utils from "../../../utils/utils";
import { calculateReviewDueDate } from "./qmsReviewDateUtils";

export const REMINDER_FREQUENCY_OPTIONS = [
  { value: "Day", label: "Day" },
  { value: "Week", label: "Week" },
  { value: "Month", label: "Month" },
];

export const REVIEW_DURATION_MODES = {
  DURATION: "duration",
  DATE: "date",
};

export const DEFAULT_REMINDER_FREQUENCY = "Day";
export const DEFAULT_REMINDER_INTERVAL = 7;

export const PUBLISH_NOTIFICATION_RECIPIENTS =
  "My Learning staff, Document library staff";
export const PUBLISH_NOTIFICATION_CHANNEL = "Email";

const REVIEW_RULE_DEFS = [
  { triggerCode: "T-30", triggerLabel: "30 days before review due", offsetDays: -30 },
  { triggerCode: "T-7", triggerLabel: "7 days before review due", offsetDays: -7 },
  { triggerCode: "T-0", triggerLabel: "Review due date", offsetDays: 0 },
  { triggerCode: "T+1", triggerLabel: "1 day overdue", offsetDays: 1 },
];

function addDays(date, days) {
  const next = new Date(date.getTime());
  next.setDate(next.getDate() + days);
  return next;
}

function startOfDay(date) {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

export function formatReviewDueDateLabel(dueDate) {
  if (!dueDate) return "";
  return utils.formatDateTimeDisplay(dueDate).split(" ")[0];
}

export function formatReviewDueDateForInput(dueDate) {
  if (!dueDate) return "";
  const normalized = startOfDay(dueDate);
  const yyyy = normalized.getFullYear();
  const mm = String(normalized.getMonth() + 1).padStart(2, "0");
  const dd = String(normalized.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function parseReviewDueDateInput(value) {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value).trim())) {
    const [yyyy, mm, dd] = String(value)
      .trim()
      .split("-")
      .map((part) => Number(part));
    const parsed = new Date(yyyy, mm - 1, dd);
    return Number.isNaN(parsed.getTime()) ? null : startOfDay(parsed);
  }
  return utils.parseDateInput(value) ? startOfDay(utils.parseDateInput(value)) : null;
}

export function resolvePublishReviewDueDate(
  reviewDateNumber,
  reviewDateSuffix,
  publishDate = new Date(),
) {
  return calculateReviewDueDate(publishDate, reviewDateNumber, reviewDateSuffix);
}

/** Best-fit Month/Year duration for API storage when user picks an exact due date. */
export function deriveReviewDurationFromDueDate(publishDate, dueDate) {
  const start = startOfDay(publishDate);
  const end = startOfDay(dueDate);
  if (!end || end <= start) return null;

  for (let years = 1; years <= 50; years += 1) {
    const candidate = new Date(start.getTime());
    candidate.setFullYear(candidate.getFullYear() + years);
    if (startOfDay(candidate).getTime() === end.getTime()) {
      return { reviewDateNumber: years, reviewDateSuffix: "Year" };
    }
    if (candidate > end) break;
  }

  for (let months = 1; months <= 600; months += 1) {
    const candidate = new Date(start.getTime());
    candidate.setMonth(candidate.getMonth() + months);
    if (startOfDay(candidate).getTime() === end.getTime()) {
      return { reviewDateNumber: months, reviewDateSuffix: "Month" };
    }
    if (candidate > end) break;
  }

  const diffDays = Math.max(1, Math.ceil((end - start) / 86400000));
  const approxMonths = Math.max(1, Math.round(diffDays / 30));
  return { reviewDateNumber: approxMonths, reviewDateSuffix: "Month" };
}

export function buildReviewNotificationRules(reviewDueDate) {
  const due = reviewDueDate ? startOfDay(reviewDueDate) : null;
  return REVIEW_RULE_DEFS.map((def, index) => {
    const scheduledDate = due ? addDays(due, def.offsetDays) : null;
    return {
      triggerCode: def.triggerCode,
      triggerLabel: def.triggerLabel,
      scheduledDate,
      scheduledDateLabel: scheduledDate ? formatReviewDueDateLabel(scheduledDate) : "—",
      recipients: PUBLISH_NOTIFICATION_RECIPIENTS,
      channel: PUBLISH_NOTIFICATION_CHANNEL,
      isEnabled: true,
      sortOrder: index,
    };
  });
}

export function buildQuizReminderSchedule(
  reviewDueDate,
  reminderFrequency,
  reminderInterval,
  fromDate = new Date(),
) {
  if (!reviewDueDate) return [];

  const due = startOfDay(reviewDueDate);
  const start = startOfDay(fromDate);
  const interval = Number(reminderInterval);
  if (!Number.isFinite(interval) || interval <= 0) return [];

  const freq = String(reminderFrequency || "Day")
    .trim()
    .toLowerCase();
  const stepDays = freq.startsWith("week")
    ? interval * 7
    : freq.startsWith("month")
      ? interval * 30
      : interval;

  const dates = [];
  let cursor = addDays(start, stepDays);
  while (cursor <= due && dates.length < 24) {
    dates.push({
      date: new Date(cursor),
      dateLabel: formatReviewDueDateLabel(cursor),
    });
    cursor = addDays(cursor, stepDays);
  }
  return dates;
}

export function formatQuizDueDateForApi(dueDate) {
  return formatReviewDueDateForInput(dueDate);
}

export function normalizeReminderInterval(value, fallback = DEFAULT_REMINDER_INTERVAL) {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return fallback;
  return Math.round(amount);
}

export function getMinReviewDueDateInput(publishDate = new Date()) {
  return formatReviewDueDateForInput(addDays(startOfDay(publishDate), 1));
}
